import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Target, Eye, Lightbulb, Heart, Shield, Users, Award, Zap, Globe, ArrowRight, Star, CheckCircle } from "lucide-react";

function About() {
  const [isVisible, setIsVisible] = useState({});
  const [activeCard, setActiveCard] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    // Auto-rotate core values cards
    const cardInterval = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % 4);
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(cardInterval);
    };
  }, []);

  const organizationalPillars = [
    {
      icon: Target,
      title: "Our Mission",
      content: "Creating A Community Free From Adversity",
      description: "We work tirelessly to eliminate barriers and create opportunities for every member of our community to thrive.",
      color: "from-red-500 to-pink-500",
      bgGradient: "from-red-50 to-pink-50"
    },
    {
      icon: Eye,
      title: "Our Vision", 
      content: "Building a community where people have pathways to unparalled life skills, experience, protection, development and participation",
      description: "We envision empowered communities where every individual has access to the tools and opportunities they need to succeed.",
      color: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50"
    },
    {
      icon: Lightbulb,
      title: "Our Philosophy",
      content: "We believe that through collective strength of people, better teamwork and adoption of local and scientific methods, can move person beyond the stigma of poverty",
      description: "Empowering individuals with the tools to reach their true and full potential through innovative solutions to social problems.",
      color: "from-green-500 to-emerald-500",
      bgGradient: "from-green-50 to-emerald-50"
    },
    {
      icon: Heart,
      title: "Our Purpose",
      content: "To abolish the differences among individuals, develop spirit of common interest and sacrifice and also participate collectively in community programs",
      description: "Building unity and fostering collaborative action for sustainable community development.",
      color: "from-purple-500 to-indigo-500",
      bgGradient: "from-purple-50 to-indigo-50"
    }
  ];

  const coreValues = [
    {
      title: "Challenge Takers",
      image: "img/challenger.jpg",
      description: "We go beyond conventional ideas and approach every situation so that new possibilities and innovation can flourish to ensure real and lasting positive change.",
      icon: Zap,
      color: "from-orange-500 to-red-500"
    },
    {
      title: "Respect",
      image: "img/respect3.jpg", 
      description: "We value and celebrate the unique and diverse talents, experiences and perspectives of everyone, and treat our participants, partners, donors and each other with sensitivity and respect.",
      icon: Users,
      color: "from-blue-500 to-purple-500"
    },
    {
      title: "Accountability",
      image: "img/5.jpg",
      description: "We hold ourselves accountable for quality and lasting results of our work and for the commitments we make to our participants, partners, donors and each other.",
      icon: Shield,
      color: "from-green-500 to-blue-500"
    },
    {
      title: "Integrity",
      image: "img/integrity.jpg",
      description: "We maintain the highest standards of professional and ethical behavior, and value transparency and honesty in our relationships and actions.",
      icon: Award,
      color: "from-purple-500 to-pink-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 overflow-hidden">
      <Header />
      
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-200/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-pink-200/20 rounded-full blur-xl animate-pulse delay-2000"></div>
        
        {/* Floating organic shapes */}
        <div className="absolute top-1/4 right-0 w-[600px] h-[600px] opacity-10">
          <svg viewBox="0 0 200 200" className="w-full h-full text-blue-300 animate-spin" style={{animationDuration: '35s'}}>
            <path fill="currentColor" d="M44.7,-76.4C58.8,-69.2,71.8,-57.1,79.6,-41.8C87.4,-26.6,90,-8.2,87.4,9.1C84.8,26.4,77,42.6,63.2,53.8C49.4,65,29.6,71.2,8.9,76.3C-11.8,81.4,-33.4,85.4,-49.8,78.1C-66.2,70.8,-77.4,52.2,-84.1,31.1C-90.8,10,-93,13.5,-89.7,-8.8C-86.4,-31.1,-77.6,-58.2,-61.2,-65.4C-44.8,-72.6,-20.8,-59.9,-0.1,-59.8C20.6,-59.7,30.6,-83.6,44.7,-76.4Z"/>
          </svg>
        </div>
        
        <div className="absolute bottom-1/3 left-0 w-[500px] h-[500px] opacity-15">
          <svg viewBox="0 0 200 200" className="w-full h-full text-purple-300 animate-spin" style={{animationDuration: '40s', animationDirection: 'reverse'}}>
            <path fill="currentColor" d="M37.1,-66.5C48.3,-58.9,57.8,-46.8,64.4,-33.1C71,-19.4,74.7,-4.1,73.9,10.7C73.1,25.5,67.8,39.8,58.2,51.2C48.6,62.6,34.7,71.1,19.2,75.8C3.7,80.5,-13.4,81.4,-28.9,76.9C-44.4,72.4,-58.3,62.5,-68.7,49.1C-79.1,35.7,-86,19.8,-87.4,3.2C-88.8,-13.4,-84.7,-30.6,-75.1,-44.2C-65.5,-57.8,-50.4,-67.8,-34.5,-72.4C-18.6,-77,-3.9,-76.2,9.7,-70.8C23.3,-65.4,25.9,-74.1,37.1,-66.5Z"/>
          </svg>
        </div>
      </div>

      {/* Hero Section with Main Image */}
      <div className="relative pt-40 pb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="hero-image"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['hero-image'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="./img/about.jpg" 
                className="w-full h-64 md:h-80 lg:h-96 object-cover" 
                alt="About Kayunga Community Club"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              
              {/* Floating glassmorphic title card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/20 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-white/30">
                <h1 className="text-4xl md:text-6xl font-black text-white mb-4">
                  About <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Our Mission</span>
                </h1>
                <p className="text-xl md:text-2xl text-white/90">
                  Discover the values, vision, and purpose that drive our commitment to community transformation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organizational Pillars Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="pillars-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['pillars-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-6 py-3 rounded-full text-sm font-semibold mb-8">
              <Globe className="w-5 h-5 mr-2" />
              Our Foundation
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              What <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Drives Us</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {organizationalPillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={index}
                  id={`pillar-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`pillar-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="group relative h-full">
                    {/* Glassmorphic container */}
                    <div className="absolute inset-0 bg-white/40 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl transform group-hover:scale-[1.02] transition-all duration-300"></div>
                    <div className="relative p-8 md:p-10 h-full">
                      <div className="flex items-start space-x-6">
                        <div className={`w-16 h-16 bg-gradient-to-r ${pillar.color} rounded-2xl flex items-center justify-center flex-shrink-0 transform group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">{pillar.title}</h3>
                          <p className="text-lg font-semibold text-gray-700 mb-4 leading-relaxed">
                            {pillar.content}
                          </p>
                          <p className="text-gray-600 leading-relaxed">
                            {pillar.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Core Values Section */}
      <div className="py-20 bg-white/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="values-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['values-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-purple-100 text-purple-800 px-6 py-3 rounded-full text-sm font-semibold mb-8">
              <Star className="w-5 h-5 mr-2" />
              Core Values
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              The Principles That <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Guide Us</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto"></div>
          </div>

          {/* Interactive Values Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              const isActive = activeCard === index;
              return (
                <div
                  key={index}
                  id={`value-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 150} ${
                    isVisible[`value-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  onMouseEnter={() => setActiveCard(index)}
                >
                  <div className={`group relative h-full transform transition-all duration-500 ${
                    isActive ? 'scale-105 z-10' : 'hover:scale-102'
                  }`}>
                    {/* Glassmorphic card with enhanced effects */}
                    <div className={`absolute inset-0 backdrop-blur-xl rounded-3xl border shadow-xl transition-all duration-300 ${
                      isActive 
                        ? 'bg-white/70 border-white/50 shadow-2xl' 
                        : 'bg-white/40 border-white/30 group-hover:bg-white/60'
                    }`}></div>
                    
                    <div className="relative p-6 md:p-8 h-full flex flex-col">
                      {/* Image with overlay */}
                      <div className="relative mb-6 rounded-2xl overflow-hidden">
                        <img 
                          src={value.image} 
                          alt={value.title}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        
                        {/* Floating icon */}
                        <div className={`absolute top-4 right-4 w-12 h-12 bg-gradient-to-r ${value.color} rounded-xl flex items-center justify-center transform transition-all duration-300 ${
                          isActive ? 'scale-110' : 'group-hover:scale-105'
                        }`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      
                      <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 text-center">{value.title}</h3>
                      <p className="text-gray-700 leading-relaxed text-center flex-grow">{value.description}</p>
                      
                      {/* Active indicator */}
                      {isActive && (
                        <div className="mt-4 flex justify-center">
                          <div className={`w-2 h-2 bg-gradient-to-r ${value.color} rounded-full animate-pulse`}></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Value indicators */}
          <div className="flex justify-center mt-12 space-x-3">
            {coreValues.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveCard(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  activeCard === index 
                    ? 'bg-purple-500 scale-125' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            {/* Glassmorphic container */}
            <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/30 shadow-2xl">
              <CheckCircle className="w-16 h-16 text-white mx-auto mb-8" />
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
                Ready to Make a Difference?
              </h2>
              <p className="text-xl text-white/90 mb-10 leading-relaxed">
                Join us in creating lasting change and building stronger communities. 
                Your support helps us continue our mission of empowerment and transformation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button 
                  onClick={() => window.location.href = '/fund-our-work'}
                  className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  Support Our Mission <ArrowRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => window.location.href = '/story'}
                  className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-lg border border-white/30 transform hover:scale-105 transition-all duration-300 hover:bg-white/30"
                >
                  Learn Our Story
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating particles animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-30 animate-ping`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 2}s`,
              animationDuration: `${4 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <Footer />
    </div>
  );
}

export default About;